import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";

import TyltLogo from "../../assets/TyltLogo.svg";
import StepperKyc from "../../components/Stepper";
import Noise from "../../assets/noise_grey.png";
import Confirmation from "../../assets/confirmation.svg";
import type { AdminInfo, } from "../../types/types";

import type {
  Director,
  Shareholder,
  Step1State,
  Step3State,
  Step4State,
  StepCorporateShareholdersState,
  StepSignatureState,
} from "../../types/types";

import ApplicantEntityInfoStep from "../components/ApplicantEntityStepOne";
import EmailIdStepTwo from "../components/EmailIdStepTwo";
import ApplicantEntityDirectorsStep from "../components/ApplicantEntityDirectorStep";
import ApplicantEntityShareholdersStep from "../components/ApplicantEntityShareholderStep";
import ApplicantCorporateEntityShareHoldersStep from "../components/ApplicantCorporateEntityShareholderStep";
import ApplicantConfirmationSignatureStep from "../components/ApplicantConfirmationSignatureStep";
import { commonServices } from "../../services/apis";
import {
  isValidApplicationId,
  useApplicationId,
} from "../../utils/useApplicationId";
import AdminNotesPanelState from "../components/AdminNotesPanel";
import HorizontalStepper from "../../components/HorizontalStepper";

type KybLoadState = "loading" | "ready" | "failed";

/* --------------------------- Initial States --------------------------- */

const emptyStep1: Step1State = {
  legalEntityName: "",
  dba: "",
  legalIdentifierNumber: "",
  companyRegNo: "",
  countryOfIncorporation: "",
  dateOfIncorporation: "",
  legalAddress: "",
  businessAddress: "",
  vatNo: "",
  taxIdentificationNo: "",
  natureOfBusiness: "",
  websiteUrl: "",
  isRegulated: "",
  regulatorName: "",
  licenceNature: "",
  licenceNumber: "",
  dateOfIssue: "",
  dateOfExpiration: "",
  countryOfIssuance: "",
  certificateOfIncorporationUrl: "",
  addressProofUrl: "",
  charterDocumentsUrls: [],
  shareholdersListUrls: [],
  directorsListUrls: [],
  additionalUrls: [],
};

const emptyStep2 = { accountAccessEmailId: "" };

const emptyDirector: Director = {
  fullName: "",
  positionTitle: "",
  dateOfBirth: "",
  nationality: "",
  passportNo: "",
  email: "",
  phone: "",
  address: "",
  isPep: "",
  addressProofUrl: "",
  passportCopyUrl: "",
  additionalUrls: []
};

const emptyStep3: Step3State = {
  directors: [{ ...emptyDirector }],
};

const emptyShareholder: Shareholder = {
  fullName: "",
  percentageOwnership: "",
  dateOfBirth: "",
  nationality: "",
  passportNo: "",
  address: "",
  isPep: "",
  addressProofUrl: "",
  passportCopyUrl: "",
  additionalUrls: []
};

const emptyStep4: Step4State = { shareholders: [{ ...emptyShareholder }] };

const emptyCorporateState: StepCorporateShareholdersState = {
  corporateShareholders: [
    {
      companyName: "",
      companyRegNo: "",
      countryOfIncorporation: "",
      dateOfIncorporation: "",
      legalAddress: "",
      businessAddress: "",
      percentageOwnership: "",
      appointedRepName: "",
      positionTitle: "",
      dateOfBirth: "",
      nationality: "",
      passportNo: "",
      address: "",
      shareholdersOver20ListUrl: "",
      additionalUrls: []
    },
  ],
  noCorporateShareholders: false,
};

const emptyAdminInfo: AdminInfo = {
  notes: [],
};

/* ------------------------------ Component ---------------------------- */

const Homepage: React.FC = () => {
  // State
  const [step1, setStep1] = useState<Step1State>(emptyStep1);
  const [step2, setStep2] = useState(emptyStep2);
  const [step3, setStep3] = useState<Step3State>(emptyStep3);
  const [step4, setStep4] = useState<Step4State>(emptyStep4);
  const [stepCorporate, setStepCorporate] =
    useState<StepCorporateShareholdersState>(emptyCorporateState);
  const [stepSignature, setStepSignature] = useState<StepSignatureState>({
    name: "",
    title: "",
    date: "",
    signatureUrl: "",
  });
  const [adminInfo, setAdminInfo] = useState<AdminInfo>(emptyAdminInfo);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [adminInfoHydrated, setAdminInfoHydrated] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [merchantName, setMerchantName] = useState<string>("");

  // JWT-based Access Control
  const [adminAccess, setAdminAccess] = useState(false);
  const [invalidAccess, setInvalidAccess] = useState(false);
  const [adminInfoLoading, setAdminInfoLoading] = useState(true);
  /** Saves must not run until GET succeeds — otherwise backend may INSERT a new row each time. */
  const [kybLoadState, setKybLoadState] = useState<KybLoadState>("loading");

  const applicationId = useApplicationId();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const adminNotesAutosaveBaselineRef = useRef<string | null>(null);
  const adminNotesRef = useRef(adminInfo.notes);
  adminNotesRef.current = adminInfo.notes;
  const [jwt, setJwt] = useState<string>("");
  /* ------------------------- JWT Access Override ------------------------ */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    setJwt(token || "");
    if (!token) return; // no token = normal user flow

    try {
      const decoded: any = jwtDecode(token);
      console.log("token format");

      // Check token shape and expiry
      if (decoded && decoded.userId && decoded.exp * 1000 > Date.now()) {
        setAdminAccess(true);
      } else {
        setInvalidAccess(true);
      }
    } catch (err) {
      console.log("incorrect token format");
      console.error("Invalid JWT:", err);
      setInvalidAccess(true);
    }
  }, []);

  /* ------------------------------- Updaters ------------------------------ */

  const updateStep1 = (patch: Partial<Step1State>) =>
    setStep1((prev) => ({ ...prev, ...patch }));
  const updateStep2 = (patch: Partial<typeof step2>) =>
    setStep2((prev) => ({ ...prev, ...patch }));
  const updateStep3 = (patch: Partial<Step3State>) =>
    setStep3((prev) => ({ ...prev, ...patch }));
  const updateStep4 = (patch: Partial<Step4State>) =>
    setStep4((prev) => ({ ...prev, ...patch }));
  const updateCorporate = (patch: Partial<StepCorporateShareholdersState>) =>
    setStepCorporate((p) => ({ ...p, ...patch }));
  const updateStepSignature = (patch: Partial<StepSignatureState>) =>
    setStepSignature((prev) => ({ ...prev, ...patch }));
  const updateAdminInfo = (patch: Partial<AdminInfo>) =>
    setAdminInfo((prev) => ({ ...prev, ...patch }));

  /* ------------------------------ Validators ----------------------------- */

  const validateStep1 = () => {
    const required = [
      step1.legalEntityName,
      step1.companyRegNo,
      step1.countryOfIncorporation,
      step1.dateOfIncorporation,
      step1.legalAddress,
      step1.natureOfBusiness,
    ];
    if (required.some((v) => !v || !String(v).trim())) return false;
    if (step1.isRegulated === "yes") {
      const regReq = [
        step1.regulatorName,
        step1.licenceNature,
        step1.licenceNumber,
        step1.dateOfIssue,
        step1.countryOfIssuance,
      ];
      if (regReq.some((v) => !v || !String(v).trim())) return false;
    }
    return true;
  };

  const validateStep2 = () => !!step2.accountAccessEmailId?.trim();

  const validateStep3 = () =>
    step3.directors.every((d) =>
      [
        d.fullName,
        d.positionTitle,
        d.dateOfBirth,
        d.nationality,
        d.passportNo,
        d.email,
        d.phone,
        d.address,
        d.isPep,
        d.addressProofUrl,
        d.passportCopyUrl,
      ].every((v) => !!String(v ?? "").trim())
    );

  const validateStep4 = () =>
    step4.shareholders.every((s) => {
      const pct = Number(s.percentageOwnership);
      const pctValid = !Number.isNaN(pct) && pct >= 0 && pct <= 100;
      return (
        pctValid &&
        [
          s.fullName,
          s.percentageOwnership,
          s.dateOfBirth,
          s.nationality,
          s.passportNo,
          s.address,
          s.isPep,
          s.addressProofUrl,
          s.passportCopyUrl,
        ].every((v) => !!String(v ?? "").trim())
      );
    });

  const validateCorporate = () => {
    if (stepCorporate.noCorporateShareholders) return true;
    return stepCorporate.corporateShareholders.every((c) => {
      const pct = Number(c.percentageOwnership);
      const pctOk = !Number.isNaN(pct) && pct >= 0 && pct <= 100;
      return (
        pctOk &&
        [
          c.companyName,
          c.companyRegNo,
          c.countryOfIncorporation,
          c.dateOfIncorporation,
          c.legalAddress,
          c.percentageOwnership,
          c.appointedRepName,
          c.positionTitle,
          c.dateOfBirth,
          c.nationality,
          c.passportNo,
          c.address,
          c.shareholdersOver20ListUrl,
        ].every((v) => String(v ?? "").trim())
      );
    });
  };

  const validateSignatureStep = () => {
    const { name, title, date } = stepSignature;
    return [name, title, date].every((v) => String(v ?? "").trim());
  };

  /* ----------------------------- Data Fetch ------------------------------ */

  const hydrateFromKybJson = useCallback((kyb: Record<string, any>) => {
    if (!kyb || typeof kyb !== "object") return;

    const step1Src = kyb.applicantEntityInformation ?? kyb.step1 ?? kyb;
    const incomingMerchantName =
      kyb.merchantName ??
      step1Src?.kyb_data?.merchantName ??
      kyb.step1?.kyb_data?.merchantName ??
      kyb.kyb_data?.merchantName ??
      "";

    const { kyb_data: _omitKybData, ...step1CleanRaw } = step1Src || {};
    setStep1({ ...emptyStep1, ...(step1CleanRaw || {}) });

    if (typeof incomingMerchantName === "string" && incomingMerchantName.trim()) {
      setMerchantName(incomingMerchantName.trim());
    }

    const emailValue =
      kyb.accountAccessEmailId ?? kyb.step2?.accountAccessEmailId ?? kyb.email ?? "";
    setStep2({ accountAccessEmailId: String(emailValue || "") });

    const step3Src =
      kyb.applicantEntityDirectors ??
      kyb.step3 ??
      (Array.isArray(kyb.directors) ? { directors: kyb.directors } : undefined);
    if (step3Src) setStep3({ ...emptyStep3, ...step3Src });

    const step4Src =
      kyb.applicantEntityShareholderDetails ??
      kyb.step4 ??
      (Array.isArray(kyb.shareholders)
        ? { shareholders: kyb.shareholders }
        : undefined);
    if (step4Src) setStep4({ ...emptyStep4, ...step4Src });

    const corpSrc =
      kyb.applicantEntityCorporateShareholderDetails ??
      kyb.stepCorporate ??
      (Array.isArray(kyb.corporateShareholders)
        ? { corporateShareholders: kyb.corporateShareholders }
        : undefined);
    if (corpSrc) setStepCorporate({ ...emptyCorporateState, ...corpSrc });

    const sigSrc =
      kyb.confirmationAndSignature ?? kyb.stepSignature ?? kyb.signature;
    if (sigSrc) setStepSignature((prev) => ({ ...prev, ...sigSrc }));

    const incomingAdminInfo = kyb.adminInfo ?? kyb.admin_info;

    if (incomingAdminInfo && typeof incomingAdminInfo === "object") {
      const notes = Array.isArray(incomingAdminInfo.notes)
        ? incomingAdminInfo.notes.filter((n: any) => n && typeof n.text === "string")
        : [];

      setAdminInfo({
        notes,
      });
    } else {
      setAdminInfo(emptyAdminInfo);
    }
  }, []);

  const getKybData = useCallback(async () => {
    try {
      setAdminInfoLoading(true);
      setKybLoadState("loading");
      if (!isValidApplicationId(applicationId)) {
        setErrorMsg(
          "This page needs a valid application id in the URL (the KYB link from your invite).",
        );
        setKybLoadState("failed");
        return;
      }
      const { data: record } = await commonServices.getInstitutionalKYB(applicationId);
      const kyb = (record as any).kybData ?? (record as any).kyb_data ?? {};
      hydrateFromKybJson(kyb);

      setSubmitted(Boolean((record as any).isSubmitted ?? (record as any).is_submitted));
      setKybLoadState("ready");
    } catch (err) {
      console.error("Fetch KYB data failed:", err);
      setErrorMsg("Failed to fetch KYB data.");
      setKybLoadState("failed");
    }
    finally {
      setAdminInfoLoading(false);
      setAdminInfoHydrated(true);
    }
  }, [applicationId, hydrateFromKybJson]);

  useEffect(() => {
    void getKybData();
  }, [getKybData]);

  useEffect(() => {
    adminNotesAutosaveBaselineRef.current = null;
  }, [applicationId]);

  /* ----------------------------- Save / Submit --------------------------- */
  const buildPayload = useCallback(() => {
    const { kyb_data: _omitKybData, ...step1Clean } = (step1 as any) || {};

    const payload: any = {
      merchantName,
      applicantEntityInformation: step1Clean,
      accountAccessEmailId: step2.accountAccessEmailId,
      applicantEntityDirectors: step3,
      applicantEntityShareholderDetails: step4,
      applicantEntityCorporateShareholderDetails: stepCorporate,
      confirmationAndSignature: stepSignature,
      submittedAt: new Date().toISOString(),
    };

    if (adminAccess && adminInfoHydrated) {
      payload.adminInfo = {
        notes: adminInfo.notes ?? [],
      };
    }

    return payload;
  }, [
    step1,
    step2,
    step3,
    step4,
    stepCorporate,
    stepSignature,
    merchantName,
    adminAccess,
    adminInfo.notes, // (optional refinement)
  ]);



  const saveKybDraft = useCallback(async () => {
    if (kybLoadState !== "ready") {
      setErrorMsg(
        "Application data is not loaded. Check that you opened the correct KYB link — saving without a valid application can create duplicate records.",
      );
      return false;
    }
    const payload = buildPayload();
    setSaving(true);
    setErrorMsg(null);

    try {
      if (adminAccess) {
        // Keep is_submitted true when admin edits an already-submitted application.
        await commonServices.saveKybDataAdmin(
          payload,
          applicationId,
          jwt,
          submitted,
        );
      } else {
        await commonServices.saveInstitutionalKYB(payload, false, applicationId);
      }
      return true;
    } catch (err: any) {
      // ✅ Handle Unauthorized error from backend
      if (err?.response?.status === 401) {
        setInvalidAccess(true);
        setErrorMsg("Unauthorized access. Please check your token.");
        return false; // 🔒 prevent next step
      }

      console.error("Save draft failed:", err);
      setErrorMsg("Failed to save draft.");
      return false;
    } finally {
      setSaving(false);
    }
  }, [buildPayload, applicationId, adminAccess, jwt, kybLoadState, submitted]);

  const saveKybDraftRef = useRef(saveKybDraft);
  saveKybDraftRef.current = saveKybDraft;

  const submitKybFinal = useCallback(async () => {
    if (kybLoadState !== "ready") {
      setErrorMsg(
        "Application data is not loaded. Open a valid KYB link before submitting.",
      );
      return false;
    }
    const payload = buildPayload();
    setSaving(true);
    setErrorMsg(null);

    try {
      if (adminAccess) {
        await commonServices.saveKybDataAdmin(payload, applicationId, jwt, true);
      } else {
        await commonServices.saveInstitutionalKYB(payload, true, applicationId);
      }
      setSubmitted(true);
    } catch (err: any) {
      // ✅ Handle Unauthorized error from backend
      if (err?.response?.status === 401) {
        setInvalidAccess(true);
        setErrorMsg("Unauthorized access. Please check your token.");
        return false; // 🔒 stop navigation
      }

      console.error("Submit KYB failed:", err);
      setErrorMsg("Failed to submit KYB.");
      return false;
    } finally {
      setSaving(false);
      // window.location.reload();
    }
  }, [buildPayload, applicationId, adminAccess, jwt, kybLoadState]);



  const handleFinish = useCallback(
    async (success?: boolean) => {
      if (success === false) {
        if (!submitted) await saveKybDraft();
        return;
      }
      if (submitted) {
        window.location.reload();
        return;
      }
      try {
        await submitKybFinal();
        setSubmitted(true);
        window.location.reload();
      } catch { }
    },
    [submitted, saveKybDraft, submitKybFinal]
  );

  useEffect(() => {
    if (!adminAccess) return;
    if (kybLoadState !== "ready") return;
    if (!adminInfoHydrated) return;
    if (submitted) return;

    const serialized = JSON.stringify(adminInfo.notes ?? []);

    if (adminNotesAutosaveBaselineRef.current === null) {
      adminNotesAutosaveBaselineRef.current = serialized;
      return;
    }

    if (adminNotesAutosaveBaselineRef.current === serialized) {
      return;
    }

    const t = setTimeout(() => {
      void (async () => {
        try {
          const ok = await saveKybDraftRef.current();
          if (ok === false) return;
          adminNotesAutosaveBaselineRef.current = JSON.stringify(
            adminNotesRef.current ?? []
          );
        } catch {
          /* errors handled inside saveKybDraft */
        }
      })();
    }, 600);

    return () => clearTimeout(t);
  }, [adminInfo.notes, adminAccess, adminInfoHydrated, submitted, kybLoadState]);


  /* ------------------------------- Render ------------------------------- */


  const steps = useMemo(
    () => [
      {
        label: "APPLICANT ENTITY INFORMATION",
        content: (
          <ApplicantEntityInfoStep
            value={step1}
            onChange={updateStep1}
            readOnly={submitted && !adminAccess}
            isAdmin={adminAccess}
          />
        ),
        validator: () => true,
        ctaText: submitted ? "Next" : "Save & Continue",
        onClick: async () => {
          if (submitted && !adminAccess) return true;
          try {
            return (await saveKybDraft()) !== false;
          } catch {
            return false;
          }
        },
      },
      {
        label: "ACCOUNT ACCESS EMAIL ID",
        content: (
          <div
            className={
              submitted && !adminAccess ? "pointer-events-none select-none" : ""
            }
          >
            <EmailIdStepTwo value={step2} onChange={updateStep2} />
          </div>
        ),
        validator: () => true,
        ctaText: submitted ? "Next" : "Save & Continue",
        onClick: async () => {
          if (submitted && !adminAccess) return true;
          try {
            return (await saveKybDraft()) !== false;
          } catch {
            return false;
          }
        },
      },
      {
        label: "APPLICANT ENTITY DIRECTORS",
        content: (
          <ApplicantEntityDirectorsStep
            value={step3}
            onChange={updateStep3}
            readOnly={submitted && !adminAccess}
            isAdmin={adminAccess}
          />
        ),
        validator: () => true,
        ctaText: submitted ? "Next" : "Save & Continue",
        onClick: async () => {
          if (submitted && !adminAccess) return true;
          try {
            return (await saveKybDraft()) !== false;
          } catch {
            return false;
          }
        },
      },
      {
        label: "APPLICANT ENTITY SHAREHOLDER DETAILS",
        content: (
          <ApplicantEntityShareholdersStep
            value={step4}
            onChange={updateStep4}
            readOnly={submitted && !adminAccess}
            isAdmin={adminAccess}
          />
        ),
        validator: () => true,
        ctaText: submitted ? "Next" : "Save & Continue",
        onClick: async () => {
          if (submitted && !adminAccess) return true;
          try {
            return (await saveKybDraft()) !== false;
          } catch {
            return false;
          }
        },
      },
      {
        label: "APPLICANT CORPORATE ENTITY SHAREHOLDERS",
        content: (
          <ApplicantCorporateEntityShareHoldersStep
            value={stepCorporate}
            onChange={updateCorporate}
            readOnly={submitted && !adminAccess}
            isAdmin={adminAccess}
          />
        ),
        validator: () => true,
        ctaText: submitted ? "Next" : "Save & Continue",
        onClick: async () => {
          if (submitted && !adminAccess) return true;
          try {
            return (await saveKybDraft()) !== false;
          } catch {
            return false;
          }
        },
      },
      {
        label: "CONFIRMATION AND SIGNATURE",
        content: (
          <div
            className={
              submitted && !adminAccess ? "pointer-events-none select-none" : ""
            }
          >
            <ApplicantConfirmationSignatureStep
              value={stepSignature}
              onChange={updateStepSignature}
            />
          </div>
        ),
        validator: () => {
          const { name, title, date } = stepSignature;
          return Boolean(
            String(name || "").trim() &&
            String(title || "").trim() &&
            String(date || "").trim()
          );
        },
        ctaText: submitted ? "Finish" : "Submit Application",
        onClick: async () => {
          if (submitted) return true; // already submitted (admin or not) → just go to step 1

          // 🔥 Open confirmation modal instead of submitting
          setShowSubmitConfirmModal(true);
          return false; // prevent next step
        },
      }

    ],
    [
      step1,
      step2,
      step3,
      step4,
      stepCorporate,
      stepSignature,
      submitted,
      adminAccess,
      updateStep1,
      updateStep2,
      updateStep3,
      updateStep4,
      updateCorporate,
      updateStepSignature,
      saveKybDraft,
      submitKybFinal,
    ]
  );

  if (invalidAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="p-6 bg-white rounded shadow text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Invalid Access
          </h2>
          <p className="text-gray-600">
            The access token provided is invalid or expired.
          </p>
        </div>
      </div>
    );
  }

  if (kybLoadState === "failed" && !adminInfoLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="p-6 bg-white rounded shadow text-center max-w-lg">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Could not load this application
          </h2>
          <p className="text-gray-600 text-sm mb-3">
            The link may be wrong, truncated, or the application no longer exists.
            Do not continue filling the form — each save without a valid loaded
            application can create a new duplicate record on the server.
          </p>
          {errorMsg && (
            <p className="text-sm text-gray-800 bg-gray-100 rounded p-2">{errorMsg}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="min-h-screen overflow-y-auto"
      style={{ backgroundImage: `url(${Noise})` }}
    >
      {/* Header */}
      <div className="border-b border-border-subtle">
        <div
          className={
            adminAccess
              ? "px-[25px] sm:px-[50px] md:px-[100px] lg:px-24 xl:px-0 py-[15px] xl:grid xl:grid-cols-[400px_minmax(0,1fr)_400px] items-center"
              : "px-[25px] sm:px-[50px] md:px-[100px] lg:px-24 xl:px-[400px] py-[15px] flex items-center justify-between"
          }
        >
          {adminAccess && <div />}
          <img src={TyltLogo} alt="Tylt Logo" />
          {adminAccess && <div />}
        </div>
      </div>

      {/* Main */}
      <div
        className={
          adminAccess
            ? "px-[25px] sm:px-6 md:px-12 lg:px-24 xl:px-0 py-[20px] xl:grid xl:grid-cols-[400px_minmax(0,1fr)_400px] xl:gap-6"
            : "px-[25px] sm:px-6 md:px-12 lg:px-24 xl:px-[400px] py-[20px] space-y-6"
        }
      >
        {/* left spacer when admin */}
        {adminAccess && <div />}

        {/* ✅ Center column (Stepper stays here ALWAYS) */}
        <div className="min-w-0 space-y-6">
          <h1 className="text-[18px] sm:text-[20px] font-normal text-text-base">
            TYLT ONBOARDING KYB – INSTITUTIONAL ACCOUNTS
          </h1>

          {errorMsg && !submitted && (
            <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              {errorMsg}
            </div>
          )}

          {submitted && !adminAccess && (
            <div className="rounded-lg border border-border-subtle p-6 bg-white mb-4">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-white"
                >
                  ✓
                </span>
                <h2 className="text-[16px] font-semibold text-text-base">
                  KYB application submitted successfully
                </h2>
              </div>
              <p className="mt-2 text-[14px] text-text-subtle">
                This application is read-only.
              </p>
            </div>
          )}

          <HorizontalStepper steps={steps} initialStepIndex={0} onFinish={handleFinish} />
        </div>

        {/* ✅ Right column (reserved 400px even if empty) */}
        {adminAccess ? (
          <aside className="hidden xl:block self-start sticky top-6">
            <div className="rounded-2xl border mr-[10px] mt-[50px] border-border-subtle bg-white p-3">
              <AdminNotesPanelState
                notes={adminInfo.notes}
                setNotes={(nextNotes) => updateAdminInfo({ notes: nextNotes })}
                isLoading={adminInfoLoading}
              />
            </div>

          </aside>
        ) : null}
      </div>

      {/* Footer */}
      <div
        className={
          adminAccess
            ? "px-0 sm:px-6 md:px-12 lg:px-24 xl:px-0 mb-4 xl:grid xl:grid-cols-[400px_minmax(0,1fr)_400px] text-[12px]"
            : "px-0 sm:px-6 md:px-12 lg:px-24 xl:px-[400px] mb-4 text-[12px] flex items-center justify-center flex-col gap-1"
        }
      >
        {/* left spacer when admin */}
        {adminAccess && <div />}

        {/* center */}
        <div className="flex items-center justify-center flex-col gap-1">
          <p className="font-bold">TYLT FINTECH SP Z.O.O</p>
          <p>UL. BARTYCKA 22B / 21A, 00-716 WARSZAWA</p>
        </div>

        {/* right spacer reserved */}
        {adminAccess && <div />}
      </div>

      {showSubmitConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 mx-6 max-w-sm w-full shadow-lg flex flex-col items-center">
            <img src={Confirmation} alt="" />
            <h2 className="text-lg font-semibold text-text-base mb-3">
              Confirm Submission
            </h2>

            <p className="text-sm text-text-subtle mb-4">
              Once you submit the KYB application, you will not be able to edit it.
              Please confirm that all information is accurate.
            </p>

            <div className="flex justify-evenly items-center gap-3">
              <button
                className="px-4 py-2 text-sm border rounded"
                onClick={() => setShowSubmitConfirmModal(false)}
              >
                Cancel
              </button>

              <button
                className="px-4 py-2 text-sm text-text-base bg-bg-button-primary rounded"
                onClick={async () => {
                  setShowSubmitConfirmModal(false);
                  await submitKybFinal();

                }}
              >
                Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default Homepage;
